import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { useTheme } from "context/ThemeContext";
import { useApi } from "context/ApiProvider";
import { useSelectedDesignStore } from "context/SelectedDesignContext";
import { clearPendingTicketFields, peekPendingTicketFields } from "Voice/voiceCommands";


interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  onTitleChange?: (value: string) => void;
  onDescriptionChange?: (value: string) => void;
}

const SupportModal: React.FC<SupportModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  onTitleChange,
  onDescriptionChange,
}) => {
  const { isDarkMode } = useTheme();
  const api = useApi();
  const [localTitle, setLocalTitle] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const supportTitle = title ?? localTitle;
  const supportDescription = description ?? localDescription;
  const setSupportTitle = onTitleChange ?? setLocalTitle;
  const setSupportDescription = onDescriptionChange ?? setLocalDescription;
  const titleRef = useRef(supportTitle);
  const descriptionRef = useRef(supportDescription);
  titleRef.current = supportTitle;
  descriptionRef.current = supportDescription;

  const handleSupportSubmit = async () => {
    const title = titleRef.current.trim();
    const description = descriptionRef.current.trim();
    if (!title || !description) {
      toast.error('Please fill in both title and description.');
      return;
    }

    const designId = useSelectedDesignStore.getState().designId;
    if (!designId) {
      toast.error('Invalid or missing Design ID.');
      return;
    }

    setIsSubmitting(true);
    try {
      const body = {
        designId: designId,
        title,
        description,
      };

      const response = await api.post('/api/feedback', body);

      if (response.ok || response.status === 200 || response.status === 201) {
        toast.success('Support ticket submitted successfully!');
        setSupportTitle('');
        setSupportDescription('');
        setLocalTitle('');
        setLocalDescription('');
        clearPendingTicketFields();
        onClose();
      } else {
        toast.error(response.body || 'Failed to submit support ticket.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || error.message || 'An error occurred while submitting the ticket.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitRef = useRef(handleSupportSubmit);
  submitRef.current = handleSupportSubmit;

  useEffect(() => {
    const onVoiceSend = () => { void submitRef.current(); };
    document.addEventListener("voice:sendTicket", onVoiceSend);
    return () => document.removeEventListener("voice:sendTicket", onVoiceSend);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const pending = peekPendingTicketFields();
    if (pending.title) setSupportTitle(pending.title);
    if (pending.description) setSupportDescription(pending.description);
  }, [isOpen]);

 return(
  <Modal open={isOpen} onClose={onClose} keepMounted aria-labelledby="support-modal-title" closeAfterTransition sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
  <Box
    sx={{
      bgcolor: isDarkMode ? '#333' : '#fff',
      color: isDarkMode ? '#fff' : '#000',
      borderRadius: 2,
      width: { xs: '95vw', md: '40vw' },
      maxHeight: { xs: '95vh', md: '80vh' },
      mx: 'auto',
      boxShadow: 24,
      outline: 'none',
      display: 'flex',
      flexDirection: 'column',
      p: 0,
      position: 'relative',
    }}
  >
    {/* Header */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        borderBottom: '1px solid #ccc',
      }}
    >
      <Typography
        variant="h6"
        id="support-modal-title"
        sx={{ color: isDarkMode ? '#fff' : '#076596' }}
      >
        Submit Support Ticket
      </Typography>

      <IconButton onClick={onClose}>
        <CloseIcon sx={{ color: isDarkMode ? '#fff' : '#000' }} />
      </IconButton>
    </Box>

    {/* Content */}
    <Box
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        p: 3,
        position: 'relative',
      }}
    >
      <TextField
        label="Title"
        fullWidth
        value={supportTitle}
        onChange={(e) => setSupportTitle(e.target.value)}
        variant="outlined"
        InputLabelProps={{
          style: { color: isDarkMode ? '#bbb' : '#666' },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: isDarkMode ? '#666' : '#ccc',
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? '#888' : '#999',
            },
            '&.Mui-focused fieldset': {
              borderColor: isDarkMode ? '#90caf9' : '#1976d2',
            },
          },
          '& .MuiInputBase-input': {
            color: isDarkMode ? '#fff' : '#000',
          },
        }}
      />

      <TextField
        label="Description"
        fullWidth
        multiline
        rows={6.5}
        value={supportDescription}
        onChange={(e) => setSupportDescription(e.target.value)}
        variant="outlined"
        InputLabelProps={{
          style: { color: isDarkMode ? '#bbb' : '#666' },
        }}
        sx={{
          mt: 1.5,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: isDarkMode ? '#666' : '#ccc',
            },
            '&:hover fieldset': {
              borderColor: isDarkMode ? '#888' : '#999',
            },
            '&.Mui-focused fieldset': {
              borderColor: isDarkMode ? '#90caf9' : '#1976d2',
            },
          },
          '& .MuiInputBase-input': {
            color: isDarkMode ? '#fff' : '#000',
          },
        }}
      />
    </Box>

    {/* Footer */}
    <Box
      sx={{
        p: 2,
        borderTop: '1px solid #ccc',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
      }}
    >
      <Button
        variant="outlined"
        onClick={onClose}
        sx={{
          color: isDarkMode ? '#fff' : '#000',
          borderColor: '#aaa',
          width: '90px',
          fontWeight: 500,
        }}
      >
        Cancel
      </Button>

      <Button
        variant="contained"
        onClick={handleSupportSubmit}
        disabled={!supportTitle || !supportDescription || isSubmitting}
        sx={{
          color: '#fff',
          backgroundColor: '#076596',
          width: '160px',
          fontWeight: 500,
          '&:hover': { backgroundColor: '#0a6fb4' },
          '&:disabled': {
            backgroundColor: isDarkMode ? '#555' : '#ccc',
            color: isDarkMode ? '#888' : '#999',
          },
        }}
      >
        {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
      </Button>
    </Box>
  </Box>
</Modal>
  );
};

export default SupportModal;